export type BoondEntityType = 'candidate' | 'resource';

export interface BoondEntityIdentityRef {
    entityType: BoondEntityType;
    entityId: string;
}

export interface BoondEntityIdentity extends BoondEntityIdentityRef {
    firstName: string;
    lastName: string;
    displayName: string;
    email?: string;
    thumbnailId?: string;
}

export interface BoondEntityIdentityCacheScope {
    companyId?: string | null;
    userId?: string | null;
}

interface BoondEntityIdentityCacheRecord extends BoondEntityIdentity {
    cacheKey: string;
    entityKey: string;
    companyId: string;
    userId: string;
    syncedAt: number;
    expiresAt: number;
}

const DB_NAME = 'nli-boond-entity-identity-cache';
const STORE_NAME = 'identities';
const DB_VERSION = 1;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const getBoondEntityIdentityKey = (entity: BoondEntityIdentityRef) => `${entity.entityType}:${entity.entityId}`;

const normalizeScopePart = (value?: string | null) => String(value || 'global');

const getCacheKey = (scope: BoondEntityIdentityCacheScope, entity: BoondEntityIdentityRef) =>
    `${normalizeScopePart(scope.companyId)}:${normalizeScopePart(scope.userId)}:${getBoondEntityIdentityKey(entity)}`;

const isIndexedDbAvailable = () => typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const openDatabase = (): Promise<IDBDatabase | null> => {
    if (!isIndexedDbAvailable()) return Promise.resolve(null);

    return new Promise((resolve) => {
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
    });
};

const withStore = async <T>(
    mode: IDBTransactionMode,
    callback: (store: IDBObjectStore) => void,
    fallback: T,
): Promise<T> => {
    const db = await openDatabase();
    if (!db) return fallback;

    return new Promise((resolve) => {
        const transaction = db.transaction(STORE_NAME, mode);
        const store = transaction.objectStore(STORE_NAME);

        transaction.oncomplete = () => {
            db.close();
            resolve(fallback);
        };
        transaction.onerror = () => {
            db.close();
            resolve(fallback);
        };
        transaction.onabort = () => {
            db.close();
            resolve(fallback);
        };

        callback(store);
    });
};

const readRecord = (store: IDBObjectStore, key: string): Promise<BoondEntityIdentityCacheRecord | undefined> =>
    new Promise((resolve) => {
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result as BoondEntityIdentityCacheRecord | undefined);
        request.onerror = () => resolve(undefined);
    });

export const boondEntityIdentityCache = {
    async getFreshMany(
        scope: BoondEntityIdentityCacheScope,
        entities: BoondEntityIdentityRef[],
    ): Promise<Record<string, BoondEntityIdentity>> {
        const db = await openDatabase();
        if (!db) return {};

        const now = Date.now();
        const keys = entities.map((entity) => getCacheKey(scope, entity));

        try {
            const transaction = db.transaction(STORE_NAME, 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const freshByEntityKey: Record<string, BoondEntityIdentity> = {};

            const records = await Promise.all(keys.map((key) => readRecord(store, key)));
            records.forEach((record) => {
                if (record && record.expiresAt > now) {
                    freshByEntityKey[record.entityKey] = {
                        entityType: record.entityType,
                        entityId: record.entityId,
                        firstName: record.firstName,
                        lastName: record.lastName,
                        displayName: record.displayName,
                    };
                }
            });

            db.close();
            return freshByEntityKey;
        } catch {
            db.close();
            return {};
        }
    },

    async upsertMany(scope: BoondEntityIdentityCacheScope, identities: BoondEntityIdentity[]) {
        const now = Date.now();

        await withStore<void>('readwrite', (store) => {
            identities.forEach((identity) => {
                const entityKey = getBoondEntityIdentityKey(identity);
                const record: BoondEntityIdentityCacheRecord = {
                    ...identity,
                    cacheKey: getCacheKey(scope, identity),
                    entityKey,
                    companyId: normalizeScopePart(scope.companyId),
                    userId: normalizeScopePart(scope.userId),
                    syncedAt: now,
                    expiresAt: now + CACHE_TTL_MS,
                };
                store.put(record);
            });
        }, undefined);
    },

    async deleteExpired() {
        const now = Date.now();

        await withStore<void>('readwrite', (store) => {
            const request = store.openCursor();
            request.onsuccess = () => {
                const cursor = request.result;
                if (!cursor) return;

                const record = cursor.value as BoondEntityIdentityCacheRecord;
                if (record.expiresAt <= now) {
                    cursor.delete();
                }
                cursor.continue();
            };
        }, undefined);
    },

    async clear() {
        await withStore<void>('readwrite', (store) => {
            store.clear();
        }, undefined);
    },
};
